import { createProductListCategoryGroups } from '@features/products/product-list-build.helpers';
import { getProductListGroupDisplayName } from '@features/products/product-list.helpers';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { getResultsByFactor } from '@shared/helpers/results.helpers';
import { scaleResultsRecordByVersionContentCoverage } from '@shared/helpers/results/content-coverage.helpers';
import { genericProductsLookup } from '@shared/generic-products';
import type { SbefScopeGroupKey } from '@shared/construction/sbef-custom-groups.data';
import type {
  IProductListGroup,
  ProductRecord,
} from '@shared/models/product.model';
import type { ElementCategoryID } from '@shared/models/element-categories.model';
import type { Baseline } from '@shared/models/project.model';
import type { ResultsRecord } from '@shared/models/unit.model';

export type TopProductItem = {
  id: string;
  label: string;
  categoryId: ElementCategoryID;
  baselineCo2e: number;
  proposalCo2e: number;
};

export type TopProductsData = {
  items: TopProductItem[];
  /** Largest co2e (baseline or proposal) across all items — bar scale reference. */
  maxCo2e: number;
};

export type BuildTopProductsOptions = {
  baselineVersion: Baseline;
  scenarioVersion: Baseline;
  baselineRecord: ResultsRecord;
  scenarioRecord: ResultsRecord;
  selectedSbefGroups: readonly SbefScopeGroupKey[];
  gfa: number;
  /** Org catalog; may be empty on print/generation routes. */
  productLookup: ProductRecord;
  count?: number;
};

type ProductEntry = { group: IProductListGroup; categoryId: ElementCategoryID };

/**
 * Names for the high-carbon products slide. Print/generation skips the org
 * catalog fetch, so {@link Baseline.products} and
 * {@link genericProductsLookup} must be included — an empty org lookup would
 * otherwise label every row "Unknown".
 */
const resolveReportProductLookup = (
  productLookup: ProductRecord,
  versions: readonly Baseline[],
): ProductRecord => {
  let lookup: ProductRecord = { ...genericProductsLookup, ...productLookup };
  for (const version of versions) {
    lookup = { ...lookup, ...version.products };
  }
  return lookup;
};

function buildProductCo2eMap(
  version: Baseline,
  resultsRecord: ResultsRecord,
  gfa: number,
  selectedSbefGroups: readonly SbefScopeGroupKey[],
): { co2eById: Record<string, number>; entries: ProductEntry[] } {
  const categoryGroups = createProductListCategoryGroups(
    version,
    resultsRecord,
    selectedSbefGroups,
  );
  const co2eById: Record<string, number> = {};
  const entries: ProductEntry[] = [];
  for (const categoryGroup of categoryGroups) {
    for (const productGroup of categoryGroup.elements) {
      const co2e = getConversionFactorValue(productGroup.results, 'co2e');
      if (co2e > 0) {
        co2eById[productGroup.id] = getResultsByFactor(co2e, gfa);
        entries.push({ group: productGroup, categoryId: categoryGroup.id });
      }
    }
  }
  return { co2eById, entries };
}

/**
 * Top high-carbon products for a proposal, scaled by version content coverage
 * and named from org catalog, version products, or generic products.
 */
export const buildTopProducts = (
  options: BuildTopProductsOptions,
): TopProductsData | undefined => {
  const {
    baselineVersion,
    scenarioVersion,
    baselineRecord,
    scenarioRecord,
    selectedSbefGroups,
    gfa,
    productLookup,
    count = 15,
  } = options;

  const baseline = buildProductCo2eMap(
    baselineVersion,
    scaleResultsRecordByVersionContentCoverage(baselineRecord, baselineVersion),
    gfa,
    selectedSbefGroups,
  );
  const scenario = buildProductCo2eMap(
    scenarioVersion,
    scaleResultsRecordByVersionContentCoverage(scenarioRecord, scenarioVersion),
    gfa,
    selectedSbefGroups,
  );

  if (baseline.entries.length === 0) return undefined;

  // Union of all entries from baseline and scenario (proposal may have products not in baseline)
  const allEntriesById = new Map<string, ProductEntry>();
  for (const entry of baseline.entries)
    allEntriesById.set(entry.group.id, entry);
  for (const entry of scenario.entries) {
    if (!allEntriesById.has(entry.group.id))
      allEntriesById.set(entry.group.id, entry);
  }

  const sorted = [...allEntriesById.values()].sort(
    (a, b) =>
      (scenario.co2eById[b.group.id] ?? 0) -
      (scenario.co2eById[a.group.id] ?? 0),
  );
  const top = sorted.slice(0, count);

  const namedLookup = resolveReportProductLookup(productLookup, [
    baselineVersion,
    scenarioVersion,
  ]);
  const items: TopProductItem[] = top.map(({ group, categoryId }) => ({
    id: group.id,
    label: getProductListGroupDisplayName(group, namedLookup),
    categoryId,
    baselineCo2e: baseline.co2eById[group.id] ?? 0,
    proposalCo2e: scenario.co2eById[group.id] ?? 0,
  }));

  const maxCo2e = Math.max(
    ...items.map((item) => Math.max(item.baselineCo2e, item.proposalCo2e)),
    1,
  );

  return { items, maxCo2e };
};
